import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import { AdminHero, SectionHead, Avatar } from '@/components/admin/ui';

interface Complaint {
  id: string;
  orderRef?: string;
  complainantName: string;
  againstName: string;
  filedByRole: 'BUYER' | 'SELLER';
  category: string;
  notes?: string;
  state: 'OPEN' | 'RESOLVED' | 'DISMISSED';
  resolution?: string;
  createdAt: string;
}

export default function Complaints() {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery<Complaint[]>({
    queryKey: ['admin', 'complaints'],
    queryFn: async () => (await api.get('/admin/complaints')).data,
  });
  const resolve = useMutation({
    mutationFn: async ({ id, state, resolution }: { id: string; state: string; resolution?: string }) =>
      (await api.post(`/admin/complaints/${id}/resolve`, { state, resolution })).data,
    onSuccess: () => { toast.success('Complaint updated.'); qc.invalidateQueries({ queryKey: ['admin', 'complaints'] }); },
    onError: () => toast.error('Could not update complaint.'),
  });

  const open = rows.filter((r) => r.state === 'OPEN');
  const closed = rows.filter((r) => r.state !== 'OPEN');
  const resolved = rows.filter((r) => r.state === 'RESOLVED').length;

  return (
    <div className="p-6 md:p-10 space-y-6">
      <AdminHero
        eyebrow="Admin · Trust & safety"
        title="Complaints"
        subtitle="Conduct complaints from buyers and sellers. Review the evidence, then resolve or dismiss."
        stats={[
          { label: 'Open', value: open.length, accent: open.length ? 'bad' : undefined },
          { label: 'Resolved', value: resolved, accent: 'ok' },
          { label: 'Total', value: rows.length },
        ]}
      />

      <div>
        <SectionHead title="Open" count={open.length} />
        {open.length === 0 ? (
          <div className="card p-8 text-center text-mute text-sm">
            <div className="text-2xl mb-1 opacity-60">🕊️</div>No open complaints - all clear.
          </div>
        ) : (
          <div className="space-y-3">
            {open.map((c) => (
              <ComplaintCard key={c.id} c={c} busy={resolve.isPending}
                onResolve={(state, resolution) => resolve.mutate({ id: c.id, state, resolution })} />
            ))}
          </div>
        )}
      </div>

      {closed.length > 0 && (
        <div>
          <SectionHead title="Closed" />
          <div className="card overflow-x-auto">
            <div className="min-w-[560px] divide-y divide-warmline">
              {closed.map((c) => (
                <div key={c.id} className="grid grid-cols-12 gap-3 p-4 text-sm items-center transition-colors hover:bg-cream-deep/50">
                  <div className="col-span-5 min-w-0">
                    <span className="text-mute mono text-[11px]">{c.orderRef ?? '-'}</span>
                    <div className="truncate">{c.complainantName} <span className="text-mute">→</span> {c.againstName}</div>
                  </div>
                  <div className="col-span-3 text-mute truncate">{c.category}</div>
                  <div className="col-span-2"><span className={`pill text-[9px] ${c.state === 'RESOLVED' ? 'pill-ok' : 'bg-cream-deep text-mute'}`}>{c.state.toLowerCase()}</span></div>
                  <div className="col-span-2 text-[11px] text-mute text-right">{new Date(c.createdAt).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ComplaintCard({ c, onResolve, busy }: { c: Complaint; onResolve: (s: string, r?: string) => void; busy: boolean }) {
  const [note, setNote] = useState('');
  return (
    <div className="card p-5 border-l-2 border-l-bad/50">
      <div className="flex items-start gap-3.5">
        <Avatar name={c.complainantName} tone="clay" />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-mute mono">{c.orderRef ?? 'No order'}</div>
          <div className="text-sm font-medium mt-0.5 flex flex-wrap items-center gap-x-1.5">
            <span className="pill pill-clay text-[9px]">{c.filedByRole} complaint</span>
            {c.complainantName} <span className="text-mute font-normal">reported</span> {c.againstName}
          </div>
          <div className="text-[13px] text-ink mt-1.5 font-medium">{c.category}</div>
          {c.notes && <p className="text-[13px] text-mute mt-1 italic">“{c.notes}”</p>}
        </div>
        <span className="text-[11px] text-mute shrink-0">{new Date(c.createdAt).toLocaleDateString()}</span>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2 pl-[52px]">
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Resolution note (optional)" className="input text-[12px] py-1.5 flex-1 min-w-[160px]" />
        <button onClick={() => onResolve('DISMISSED', note)} disabled={busy} className="btn btn-sm btn-outline">Dismiss</button>
        <button onClick={() => onResolve('RESOLVED', note)} disabled={busy} className="btn btn-sm btn-gold !text-ink">Mark resolved</button>
      </div>
    </div>
  );
}
